import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { api } from '../../api/client';
import { Search, UserPlus, Check } from 'lucide-react';

interface InviteUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    targetId: string;
    targetType: 'channel' | 'topic';
    currentMemberIds: string[];
    pendingMemberIds?: string[];
}

export default function InviteUserModal({ isOpen, onClose, targetId, targetType, currentMemberIds, pendingMemberIds = [] }: InviteUserModalProps) {
    const { users } = useApp();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const availableUsers = users.filter(u =>
        !(Array.isArray(currentMemberIds) ? currentMemberIds : []).includes(u.id) &&
        !(pendingMemberIds && (Array.isArray(pendingMemberIds) ? pendingMemberIds : []).includes(u.id))
    );

    const filteredUsers = availableUsers.filter(u =>
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const toggleUser = (userId: string) => {
        setSelectedUserIds(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleInvite = async () => {
        if (selectedUserIds.length === 0) return;
        setSaving(true);
        try {
            if (targetType === 'channel') {
                await api.inviteToChannel(targetId, selectedUserIds);
            } else {
                await api.inviteToTopic(targetId, selectedUserIds);
            }

            onClose();
            setSelectedUserIds([]);
        } catch (error) {
            console.error('Failed to invite users', error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()}>
                <header className="modal-header">
                    <h2 className="modal-title">Invite Users</h2>
                    <button onClick={onClose} className="btn btn-ghost btn-icon">✕</button>
                </header>

                <div className="modal-body">
                    <div className="modal-search-wrapper">
                        <Search size={16} className="modal-search-icon" />
                        <input
                            className="modal-search-input"
                            placeholder="Search users..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <div className="modal-list-container">
                        {filteredUsers.length === 0 ? (
                            <div className="empty-state" style={{ padding: '20px' }}>
                                <div className="empty-state-text" style={{ marginBottom: 0 }}>
                                    No users found to invite.
                                </div>
                            </div>
                        ) : (
                            filteredUsers.map(user => (
                                <div
                                    key={user.id}
                                    onClick={() => toggleUser(user.id)}
                                    className={`user-list-item ${selectedUserIds.includes(user.id) ? 'active' : ''}`}
                                >
                                    <div className="avatar avatar-sm">
                                        {user.name.charAt(0)}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>{user.name}</div>
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{user.email || 'No email'}</div>
                                    </div>
                                    {selectedUserIds.includes(user.id) && <Check size={16} color="var(--accent-primary)" strokeWidth={2.5} />}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="modal-footer">
                    <button onClick={onClose} className="btn btn-secondary">Cancel</button>
                    <button
                        onClick={handleInvite}
                        disabled={selectedUserIds.length === 0 || saving}
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                        <UserPlus size={16} />
                        {saving ? 'Inviting...' : `Invite ${selectedUserIds.length > 0 ? `(${selectedUserIds.length})` : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
